<template>
    <vx-modal v-model:visible="visible" size="sm" padding="standard">
        <template v-slot:header> Move All Groups </template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="props.sourceOrgUnit?.name" :closeable="false" bordered>
                Moving from <strong class="font-bold">{{ props.sourceOrgUnit.name }}</strong>
            </vx-alert>

            <div class="mb-3">
                <vx-label :id="destinationLabelId" size="lg"> Destination </vx-label>
                <vx-radio-selection size="lg" v-model="destination" :aria-labelledby="destinationLabelId">
                    <vx-radio-selection-item value="existing">
                        <span class="font-normal">Existing OU</span>
                    </vx-radio-selection-item>

                    <vx-radio-selection-item value="new">
                        <span class="font-normal">New OU</span>
                    </vx-radio-selection-item>

                    <vx-radio-selection-item value="remove">
                        <span class="font-normal">Remove all groups from OU</span>
                    </vx-radio-selection-item>
                </vx-radio-selection>
            </div>

            <div v-if="destination === 'existing'">
                <vx-label size="lg">Select Organizational Unit</vx-label>
                <select-org-unit
                    v-model="targetOrgUnit"
                    relativeDropdown
                    size="lg"
                    placeholder=""
                    data-test="org-unit-selectable"
                    :error="displayFirstError(validation.targetOrgUnit.$errors)"
                ></select-org-unit>
            </div>

            <div v-if="destination === 'new'">
                <vx-label size="lg">Name for the new OU</vx-label>
                <vx-input
                    data-test="new-target-name"
                    v-model="newTargetOrgUnitName"
                    :error="displayFirstError(validation.newTargetOrgUnitName.$errors)"
                    size="lg"
                ></vx-input>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    data-test="cancel-button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit"
                    data-test="move-button"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Move <font-awesome-icon widthAuto :icon="faCircleArrowUpRight"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { ref, useId, watch } from 'vue';
import {
    VxAlert,
    VxButton,
    VxInput,
    VxLabel,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
} from '@voxie/frontend-components';
import { orgUnitsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { faCircleArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import SelectOrgUnit from '~/components/general/SelectOrgUnit.vue';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { alphanumericDashUnderscoreSpace, displayFirstError, validateRules } from '~/utils/validation';

const visible = defineModel('visible', {
    type: Boolean,
    default: true,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    sourceOrgUnit: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['completed']);

const toasted = useToasted();
const submitting = ref(false);
const { $externalResults, catchError } = useFormRequestHandler();

const destination = ref('existing'); // existing|new|remove
const destinationLabelId = useId();

const newTargetOrgUnitName = ref('');
const targetOrgUnit = ref(null);

const validation = useVuelidate(
    {
        newTargetOrgUnitName: {
            required_new: requiredIf(() => destination.value === 'new'),
            maxLength: maxLength(80),
            format: alphanumericDashUnderscoreSpace,
        },
        targetOrgUnit: {
            required_existing: requiredIf(() => destination.value === 'existing'),
        },
    },
    {
        newTargetOrgUnitName,
        targetOrgUnit,
    },
    { $externalResults }
);

watch(visible, () => {
    validation.value.$reset();
    newTargetOrgUnitName.value = '';
    targetOrgUnit.value = null;
});

const moveToExistingOu = async () => {
    await orgUnitsClient.bulkMoveGroups(props.teamId, props.sourceOrgUnit.id, targetOrgUnit.value.id);
    toasted.success(`All groups where moved successfully to ${targetOrgUnit.value.name}.`);
};

const moveToNewOu = async () => {
    const response = await orgUnitsClient.createOrgUnit(props.teamId, { name: newTargetOrgUnitName.value });
    targetOrgUnit.value = response.data;
    await moveToExistingOu();
};

const removeFromOu = async () => {
    await orgUnitsClient.bulkRemoveGroups(props.teamId, props.sourceOrgUnit.id);
    toasted.success(`All groups where removed successfully from ${props.sourceOrgUnit.name}`);
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validateRules(validation);
    if (!isValid) {
        return;
    }

    submitting.value = true;

    try {
        if (destination.value === 'existing') {
            await moveToExistingOu();
        } else if (destination.value === 'new') {
            await moveToNewOu();
        } else if (destination.value === 'remove') {
            await removeFromOu();
        }
        emit('completed');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
